<template>
  <div class="export-download">
    <div
      v-if="loading"
      class="export-download__loading"
    >
      <i class="far fa-spinner-third fa-spin" />
      <span>{{ t('download.pending') }}</span>
    </div>
    <div
      v-else-if="error"
      class="export-download__error"
    >
      <i class="far fa-exclamation-triangle" />
      <span>{{ error }}</span>
      <div class="export-download__redirect-info">
        {{ t('download.redirectInfo', { seconds: countdown }) }}
      </div>
      <button
        class="export-download__redirect-button"
        @click="redirectToExports"
      >
        {{ t('download.goToExports') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, onUnmounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import { triggerBlobDownload } from '@/helpers/helpers';

export default defineComponent({
  name: 'ExportDownload',

  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();

    const loading = ref(true);
    const error = ref<string | null>(null);
    const countdown = ref(10);
    let countdownInterval: ReturnType<typeof setInterval> | null = null;

    const redirectToExports = () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      router.push({ name: 'users.exports' });
    };

    const startCountdown = () => {
      countdown.value = 10;
      countdownInterval = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          redirectToExports();
        }
      }, 1000);
    };

    const downloadExport = async () => {
      const downloadType = route.params.downloadType as string;
      const exportId = route.params.idExport as string;

      if (!exportId || !downloadType) {
        error.value = t('download.error');
        loading.value = false;
        return;
      }

      try {
        toast.add({
          severity: 'info',
          summary: '',
          detail: t('download.pending'),
          life: 3000,
        });

        // Call API using nestCall with responseType: 'blob'
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/${downloadType}/download/${exportId}`,
          {},
          '',
          undefined,
          true,
          {},
          { responseType: 'blob' },
        );

        if (!response || response.error || !response.data) {
          error.value = response?.error?.message || t('download.error');
          loading.value = false;
          await showToastError(error.value || t('download.error'));
          startCountdown();
          return;
        }

        // Determine file extension from Content-Disposition header
        let fileExtension = 'csv';
        const contentDisposition = response.headers?.['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            const serverFilename = filenameMatch[1].replace(/['"]/g, '');
            const ext = serverFilename.split('.').pop()?.toLowerCase();
            if (ext === 'zip') {
              fileExtension = 'zip';
            }
          }
        }

        // Use standardized filename: export-{id}.{extension}
        const filename = `export-${exportId}.${fileExtension}`;
        const contentType = fileExtension === 'zip' ? 'application/zip' : 'text/csv';

        // Create blob with correct MIME type
        const blob = new Blob([response.data], { type: contentType });

        // Trigger the download
        triggerBlobDownload(blob, filename);
        await showToastSuccess(t('download.success'));

        loading.value = false;

        // Redirect to home after download
        setTimeout(() => {
          router.push({ name: 'users.exports' });
        }, 1500);
      } catch (err: unknown) {
        console.error('Export download error:', err);
        loading.value = false;
        error.value = t('download.error');
        await showToastError(error.value || t('download.error'));
        startCountdown();
      }
    };

    onMounted(async () => {
      await downloadExport();
    });

    onUnmounted(() => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    });

    return {
      t,
      loading,
      error,
      countdown,
      redirectToExports,
    };
  },
});
</script>

<style lang="scss" scoped>
.export-download {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f5f7fa;

  &__loading,
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    i {
      font-size: 2rem;
    }

    span {
      font-size: 1rem;
      color: #4f5d75;
    }
  }

  &__loading i {
    color: $brand-color-primary;
  }

  &__error i {
    color: #e74c3c;
  }

  &__redirect-info {
    font-size: 0.875rem;
    color: #718096;
    margin-top: 0.5rem;
  }

  &__redirect-button {
    margin-top: 1rem;
    padding: 0.75rem 1.5rem;
    background-color: $brand-color-primary;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: darken($brand-color-primary, 10%);
    }
  }
}
</style>
